'use no memo';

/* eslint-disable react/jsx-no-constructed-context-values */
import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { find } from 'lodash-es';

import { RestaurantContext } from 'context/RestaurantContext';
import { OrderContext } from 'context/OrderContext';

import {
  getUserMembershipPlan,
  getHasActiveMembership,
  getExpiredMemberships,
  getRewardsOptInStatusFromSessionStorage,
  setRewardsOptInStatusFromSessionStorage,
} from 'helpers/customer';
import {
  handleFetchLogout,
  handleFetchUserProfile,
  handleFetchUpdateUser,
  handleFetchRewards,
} from 'helpers/customerApi';
import {
  APPLE_PAY_STRUCT,
  GOOGLE_PAY_STRUCT,
  NEW_CARD_VALUE,
  creditCardStringToType,
} from 'helpers/payments';
import { handleDeliveryOrderFromURLParam } from 'helpers/restaurantApi';
import { FULFILLMENT_METHODS } from 'helpers/order';

const initialCardForm = {
  address: {
    zip: '',
  },
  cvv: '',
  expDate: '',
  is_visible: true,
  number: '',
  type: '',
};

const UserContext = createContext({
  cardForm: initialCardForm,
  handleFetchUserProfile: () => {},
  handleLogout: () => {},
  handleUpdateUser: () => {},
  isLoaded: false,
  isUserLoaded: false,
  isLoggedIn: false,
  /** @type {import("types/customer").CardType} */
  selectedCard: {},
  selectedCardId: null,
  sessionId: '',
  setCardForm: () => {},
  setIsLoaded: () => {},
  setSelectedCardId: () => {},
  setSessionId: () => {},
  setUser: () => {},
  /** @type {import("types/customer").UserDataType} */
  user: { id: '', email: '', phone: { number: '' }, is_optout: false },
});

function UserProvider(props) {
  const { children } = props;

  const {
    handleFetchRestaurant,
    restaurant,
    setTriggerHandleDeliveryOrderFromURLParam,
    triggerHandleDeliveryOrderFromURLParam,
  } = useContext(RestaurantContext);
  const isDeliveryOnly = restaurant?.delivery_only;

  const { order, updateOrder } = useContext(OrderContext);
  const optInRewardsStatus = getRewardsOptInStatusFromSessionStorage();

  const [isLoaded, setIsLoaded] = useState(false);
  const [userState, setUserState] = useState({});
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [cardForm, setCardForm] = useState(initialCardForm);
  const [selectedMembership, setSelectedMembership] = useState();
  const [rewards, setRewards] = useState([]);
  const [rewardsOptIn, setRewardsOptIn] = useState(optInRewardsStatus);

  const fetchUserValues = { sessionId, setUser: setUserState, setIsLoaded };
  const membershipValues = {
    userMemberships: userState.memberships || [],
    restaurant,
  };

  useEffect(() => {
    if (triggerHandleDeliveryOrderFromURLParam) {
      handleDeliveryOrderFromURLParam(restaurant, userState);
      setTriggerHandleDeliveryOrderFromURLParam(false);
    }
  }, [triggerHandleDeliveryOrderFromURLParam]);

  function getSelectedCard() {
    if (selectedCardId === NEW_CARD_VALUE) {
      return cardForm;
    }

    if (selectedCardId === APPLE_PAY_STRUCT.id) {
      return APPLE_PAY_STRUCT;
    }

    if (selectedCardId === GOOGLE_PAY_STRUCT.id) {
      return GOOGLE_PAY_STRUCT;
    }

    return find(userState.billing?.cards, { id: selectedCardId });
  }

  function handleSetCardForm(data) {
    if (!data) return setCardForm(initialCardForm);

    const { type } = data;

    return setCardForm({
      ...data,
      id: NEW_CARD_VALUE,
      type_id: creditCardStringToType[type],
      last_four: data.number.slice(-4),
    });
  }

  function fetchUser() {
    handleFetchUserProfile(fetchUserValues);
  }

  function fetchRewards({ id = '', param = '', includeOptOuts }) {
    handleFetchRewards({ id, param, includeOptOuts, setRewards });
  }

  function updateUser(values) {
    const error = handleFetchUpdateUser(values, userState, fetchUserValues);
    return error;
  }

  function handleSetRewardsOptIn({ optInStatus, restaurantId }) {
    setRewardsOptIn({ optInStatus, restaurantId });
    setRewardsOptInStatusFromSessionStorage({ optInStatus, restaurantId });
  }

  function handleLogout() {
    handleFetchLogout({
      setUser: setUserState,
      setCardForm,
      setSelectedCardId,
      setSessionId,
      order,
      isDeliveryOnly,
      handleFetchRestaurant,
    });

    updateOrder({
      promo_code: null,
      ...(order?.fulfill_method === FULFILLMENT_METHODS.delivery &&
        !restaurant.delivery_only && {
          fulfill_method: FULFILLMENT_METHODS.pickup,
        }),
    });
  }

  return (
    <UserContext.Provider
      value={{
        cardForm,
        handleFetchUserProfile: fetchUser,
        handleLogout,
        handleUpdateUser: updateUser,
        handleFetchRewards: fetchRewards,
        isUserLoaded: isLoaded,
        isLoggedIn: !!userState.id,
        membership: {
          userMembershipPlan: getUserMembershipPlan(membershipValues),
          hasActiveMembership: getHasActiveMembership(membershipValues),
          expiredMemberships: getExpiredMemberships(membershipValues),
          selectedMembership,
          setSelectedMembership,
        },
        rewards,
        rewardsOptIn,
        selectedCard: getSelectedCard(),
        selectedCardId,
        sessionId,
        setCardForm: handleSetCardForm,
        setIsLoaded,
        setSelectedCardId,
        setSessionId,
        setRewards,
        setRewardsOptIn: handleSetRewardsOptIn,
        setUser: setUserState,
        user: userState,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { UserContext, UserProvider };
